import { AdnReport } from '../../types/adnReport';
import { X, Edit, Download } from 'lucide-react';
import { exportAdnReportPdf } from '../../utils/exportPdf';
import { useAuth } from '../../contexts/AuthContext';
import { canEditReport } from '../../utils/permissions';

interface AdnReportPreviewProps {
  report: AdnReport;
  onClose: () => void;
  onEdit?: (report: AdnReport) => void;
}

export function AdnReportPreview({ report, onClose, onEdit }: AdnReportPreviewProps) {
  const { user } = useAuth();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Rapport ADN - {new Date(report.date).toLocaleDateString('fr-FR')}</h2>
          <div className="flex gap-2">
            {onEdit && canEditReport(report, user) && (
              <button
                onClick={() => onEdit(report)}
                className="flex items-center gap-2 px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] transition-colors"
                title="Modifier ce rapport"
              >
                <Edit size={18} />
                <span className="hidden sm:inline">Modifier</span>
              </button>
            )}
            <button
              onClick={() => exportAdnReportPdf(report)}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#00665C] text-white text-sm rounded-lg hover:bg-[#004d45] transition-colors"
              title="Télécharger en PDF"
            >
              <Download size={16} />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Résumé */}
          <section className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Résumé du dimanche</h3>
            <p className="text-blue-800">
              <strong>Nombre de nouvelles personnes ce dimanche :</strong> {report.totalNewVisitors} personne{report.totalNewVisitors !== 1 ? 's' : ''}
            </p>
          </section>

          {/* Détails des nouvelles personnes */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Détails des nouvelles personnes</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 font-medium">{report.newVisitors.men} hommes</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg">
                <p className="text-pink-800 font-medium">{report.newVisitors.women} femmes</p>
              </div>
            </div>
          </section>

          {/* Décisions des nouvelles personnes */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Décision des nouvelles personnes de dimanche</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <span className="text-yellow-800">Sont indécis</span>
                <span className="font-semibold text-yellow-900">{report.visitorDecisions.undecided}</span>
              </div>
              
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-800 font-medium">Veulent devenir membres</span>
                  <span className="font-semibold text-green-900">{report.totalWantsToJoin}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-green-700">
                    • {report.visitorDecisions.wantsToJoin.men} homme{report.visitorDecisions.wantsToJoin.men !== 1 ? 's' : ''}
                  </div>
                  <div className="text-green-700">
                    • {report.visitorDecisions.wantsToJoin.women} femme{report.visitorDecisions.wantsToJoin.women !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              
              <div className="bg-[#00665C]/8 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#004d45] font-medium">Veulent donner leur vie à Jésus</span>
                  <span className="font-semibold text-[#004d45]">{report.totalWantsToGiveLifeToJesus}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-[#00665C]">
                    • {report.visitorDecisions.wantsToGiveLifeToJesus.men} homme{report.visitorDecisions.wantsToGiveLifeToJesus.men !== 1 ? 's' : ''}
                  </div>
                  <div className="text-[#00665C]">
                    • {report.visitorDecisions.wantsToGiveLifeToJesus.women} femme{report.visitorDecisions.wantsToGiveLifeToJesus.women !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Notes */}
          {report.notes && (
            <section>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notes / Observations</h3>
              <p className="whitespace-pre-wrap text-gray-700 bg-gray-50 p-4 rounded-lg">{report.notes}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}